/**
 * QuickStatsCard - Enhanced dashboard quick stats
 * Shows NET revenue (excluding 12.5% VAT) as the primary figure
 */
import { useState, useEffect } from 'react';
import { 
  DollarSign, ShoppingBag, TrendingUp, TrendingDown, Minus,
  Star, AlertTriangle, RefreshCw, Receipt
} from 'lucide-react';
import { api } from '@/utils/apiClient';

export default function QuickStatsCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    // Refresh every 5 minutes
    const interval = setInterval(fetchStats, 300000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/dashboard/quick-stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch quick stats:', err);
    }
    setLoading(false);
  };

  const TrendIcon = ({ direction }) => {
    if (direction === 'up') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (direction === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-card rounded-2xl p-6 border border-border animate-pulse h-32" />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="quick-stats">
      {/* NET Revenue - What the cafe keeps */}
      <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <DollarSign className="w-5 h-5" />
          </div>
          <div className="flex items-center gap-1">
            <TrendIcon direction={stats.comparison.sales_direction} />
            <span className={`text-sm font-medium ${
              stats.comparison.sales_direction === 'up' ? 'text-green-100' : 
              stats.comparison.sales_direction === 'down' ? 'text-red-200' : 'text-white/70'
            }`}>
              {stats.comparison.sales_change > 0 ? '+' : ''}{stats.comparison.sales_change}%
            </span>
          </div>
        </div>
        <div className="text-3xl font-bold mb-1">
          ${stats.today.net_revenue?.toFixed(2) || '0.00'}
        </div>
        <div className="text-green-100 text-sm font-medium">Net Revenue Today</div>
        <div className="mt-2 pt-2 border-t border-white/20 text-xs text-green-100/80 space-y-0.5">
          <div className="flex justify-between">
            <span>Collected:</span>
            <span>${stats.today.gross_revenue?.toFixed(2) || '0.00'}</span>
          </div>
          <div className="flex justify-between">
            <span>VAT (12.5%):</span>
            <span>-${stats.today.vat_amount?.toFixed(2) || '0.00'}</span>
          </div>
        </div>
      </div>

      {/* Orders */}
      <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <ShoppingBag className="w-5 h-5" />
          </div>
          <div className="flex items-center gap-1">
            <TrendIcon direction={stats.comparison.orders_direction} />
            <span className={`text-sm font-medium ${
              stats.comparison.orders_direction === 'up' ? 'text-green-100' : 
              stats.comparison.orders_direction === 'down' ? 'text-red-200' : 'text-white/70'
            }`}>
              {stats.comparison.orders_change > 0 ? '+' : ''}{stats.comparison.orders_change}%
            </span>
          </div>
        </div>
        <div className="text-3xl font-bold mb-1">
          {stats.today.orders}
        </div>
        <div className="text-blue-100 text-sm">Orders Today</div>
      </div>

      {/* Top Seller */}
      <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <Star className="w-5 h-5" />
          </div>
          <span className="text-sm font-medium text-amber-100">
            {stats.top_seller.quantity} sold
          </span>
        </div>
        <div className="text-lg font-bold mb-1 truncate">
          {stats.top_seller.name}
        </div>
        <div className="text-amber-100 text-sm">Top Seller Today</div>
      </div>

      {/* Alerts */}
      <div className="bg-card rounded-2xl p-6 border border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <button 
            onClick={fetchStats}
            className="text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Pending Orders</span>
            <span className={`font-medium ${
              stats.alerts.pending_orders > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'
            }`}>
              {stats.alerts.pending_orders}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Low Stock Items</span>
            <span className={`font-medium ${
              stats.alerts.low_stock_items > 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'
            }`}>
              {stats.alerts.low_stock_items}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Loyalty Members</span>
            <span className="font-medium text-foreground">{stats.alerts.active_loyalty_members}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
